import React from "react";
import MUIBox from "@mui/material/Box";
import MUIContainer from "@mui/material/Container";
import Setting from "./Utility/Setting";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import MainContext from "./Context/MainContext/MainContext";
import { getPagesConfig } from "./Component/AppBar";
import DialogNavigation from "./Component/Dialog/DialogNavigation";

export default class Layer extends React.Component {
    static contextType = MainContext; // For legacy class components

    constructor(props) {
        super(props);

        this.state = {
            mounted: false,
        };
    }

    /**
     * Context is not available in the constructor, so update the state with
     * this info after the initial render.
     *
     * Do not override this; just use afterComponentDidMount().
     */
    componentDidMount() {
        const url = new URL(window.location);
        if (
            this.context.loaded === true ||
            url.pathname === "/authorize" ||
            url.pathname === "/" ||
            url.pathname === "/load" ||
            url.pathname === "/logout"
        ) {
            this.setState({
                mounted: true
            });
            document.body.scrollTo(0, 0);
            this.afterComponentDidMount();
        }
    }

    /**
     * Override this in the subclass if needed.
     */
    afterComponentDidMount() {
        return null;
    }

    /**
     * Render a layer inside some containers. Requires this component to be
     * mounted before rendering anything. To further explain: Layers are often
     * reading data from context and placing it in state to be mutated. Context
     * is not accessible in the constructor, so it must be done in
     * componentDidMount(). This means that the component will render, mount,
     * set state, then render again. The second render isn't a performance
     * problem, but it does require coding around the missing data which is
     * annoying. To fix that, this basically only performs the second render.
     */
    render() {
        this.setDocumentTitle();
        this.copyCredentialsToSessionStorage();

        const url = new URL(window.location);

        // If you are not logged in and you're not on one of these layers,
        // redirect to the login layer.

        if (
            !Setting.get("session") &&
            url.pathname !== "/authorize" &&
            url.pathname !== "/" &&
            url.pathname !== "/load"
        ) {
            return (
                <Redirect to={{ pathname: "/", state: { from: url.pathname } }} />
            );
        }

        /**
         * If Sales Pilot is not loaded and you're not on one of these layers,
         * redirect to the loading layer.
         */
        if (
            !this.context.loaded &&
            url.pathname !== "/authorize" &&
            url.pathname !== "/" &&
            url.pathname !== "/load" &&
            url.pathname !== "/logout"
        ) {
            return (
                <Redirect to={{ pathname: "/load", state: { from: url.pathname } }} />
            );
        }

        if (this.state.mounted === true) {
            return (
                <>
                    <MUIBox
                        sx={{
                            bgcolor: this.getBackgroundColor(),
                            paddingTop: 2,
                            paddingBottom: 2,
                            paddingLeft: 2,
                            paddingRight: 2,
                            minHeight: "calc(100vh - 50px)",
                        }}
                    >
                        <MUIContainer
                            disableGutters
                            maxWidth={false}
                            sx={{
                                maxWidth: this.getMaxWidth(),
                            }}
                        >
                            {this.renderHeader()}
                            {this.renderContent()}
                        </MUIContainer>
                    </MUIBox>
                    <DialogNavigation
                        open={Boolean(this.context.navigationOpen)}
                        onClose={() => this.context.setContextItem("navigationOpen", false)}
                        pages={getPagesConfig({}, this.context)}
                    />
                </>
            );
        }

        return null;
    }

    /**
     * Render the header. Defaults to null; subclass should override.
     */
    renderHeader() {
        return null;
    }

    /**
     * Render the content. Defaults to null; subclass should override.
     */
    renderContent() {
        return null;
    }

    /**
     * @returns The max width of this page. Overrideable by child layers.
     * Defaults to an absurdly large number for no max width.
     */
    getMaxWidth() {
        return 9999;
    }

    /**
     * @returns The background color of this page. Overrideable by child layers.
     * Defaults to the default background color for the current theme.
     */
    getBackgroundColor() {
        return "background.default";
    }

    /**
     * Copies a few things from localStorage into sessionStorage. Basically,
     * when you authorize, the session, organization, and userprofile are stored
     * in localStorage. This allows you to open new tabs and persist the same
     * credentials so you don't have to log in every time. However, if you log
     * in to Rehab in one tab, then UAT in another tab, it would be bad to
     * replace those credentials. The Rehab tab would break, or at worst do bad
     * things to the database. To fix that, the API always pulls the credentials
     * out of sessionStorage, so each tab behaves as it's own unique instance.
     */
    copyCredentialsToSessionStorage() {
        const url = new URL(window.location);
        if (
            (
                !Setting.get("session", true, window.sessionStorage) ||
                !Setting.get("organization", true, window.sessionStorage) ||
                !Setting.get("userprofile", true, window.sessionStorage)
            ) &&
            (
                Setting.get("session", true, window.localStorage) &&
                Setting.get("organization", true, window.localStorage) &&
                Setting.get("userprofile", true, window.localStorage)
            ) &&
            (
                url.pathname !== "/authorize" &&
                url.pathname !== "/"
            )
        ) {
            Setting.set("session", Setting.get("session", true, window.localStorage), window.sessionStorage);
            Setting.set("organization", Setting.get("organization", true, window.localStorage), window.sessionStorage);
            Setting.set("userprofile", Setting.get("userprofile", true, window.localStorage), window.sessionStorage);
        }
    }

    /**
     * Sets the document title and adds the company to it.
     */
    setDocumentTitle() {
        const url = new URL(window.location);
        if (
            url.pathname === "/authorize" ||
            url.pathname === "/"
        ) {
            document.title = "Sales Pilot";
        } else {
            let displayedCompany;
            switch (Setting.get("organization")) {
                case 'rehabmedical':
                    displayedCompany = 'Rehab Medical';
                    break;
                case 'corkmedical':
                    displayedCompany = 'Cork Medical';
                    break;
                case 'uat':
                    displayedCompany = 'UAT';
                    break;
                case 'localhost':
                    displayedCompany = 'UAT [localhost]';
                    break;
                default:
                    displayedCompany = Setting.get("organization");
                    break;
            }
            document.title = "Sales Pilot " + (displayedCompany ? ` • ${displayedCompany}` : "");
        }
    }
}

